<template>
  <v-card flat class="my-0">
    <v-card-text>
      <v-textarea
        rows="1"
        v-model="field.cnnStr"
        :auto-grow="true"
        v-bind:label="$t('ConnectionString')"
        :error-messages="cnnStrErrors"
        @input="validator.field.cnnStr.$touch()"
        @blur="validator.field.cnnStr.$touch()"
        @change="queryHasChanged"
      />
      <v-tabs v-model="selectedTab">
        <v-tab>{{ $t("Query") }}</v-tab>
        <v-tab>{{ $t("Site.ContentType.InternalExternalTablesFields.FastResultsQuery") }}</v-tab>
        <v-tab>{{ $t("NoResultQuery") }}</v-tab>
        <v-tab>{{ $t("Site.ContentType.InternalExternalTablesFields.NoResultPerformanceQuery") }}</v-tab>
        <v-tab-item>
          <div class="sql-editor">
            <codemirror v-model="field.command" :options="cmOption" @input="queryHasChanged" />
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="sql-editor">
            <codemirror v-model="field.performanceQuery" :options="cmOption" @input="queryHasChanged" />
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="sql-editor">
            <codemirror v-model="field.noResultQuery" :options="cmOption" @input="queryHasChanged" />
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="sql-editor">
            <codemirror v-model="field.noResultPerformanceQuery" :options="cmOption" @input="queryHasChanged" />
           </div>
        </v-tab-item>
      </v-tabs>
      <v-row>
        <v-col v-if="selectedTab == 1 || selectedTab == 3" align-self="center">
          <v-select
              v-model="selectedFilter"
              :items="filters"
              :label="$t('Filter')"
              item-text="text"
              item-value="id"
          ></v-select>
        </v-col>
        <v-col v-if="selectedTab == 1 || selectedTab == 3" cols="1" align-self="center">
          <v-btn @click="addPerformanceQueryFilter" small color="primary">
            <v-icon>mdi-plus</v-icon></v-btn
          >
        </v-col>
        <v-col>
          <v-switch
            v-model="field.multiValue"
            v-bind:label="$t('Multivalue')"
            @change="multiValue"
          ></v-switch>
        </v-col>
        <v-col>
          <v-switch
            v-model="field.useOdbc"
            v-bind:label="$t('ODBC')"
            input-value="true"
          />
        </v-col>
        <v-col cols="4">
          <div class="d-flex align-center">
            <v-select
              :loading="loading"
              v-model="field.default"
              :multiple="field.multiValue"
              v-bind:label="$t('DefaultValue')"
              item-text="text"
              item-value="id"
              :items="defaultValuesOptions"
              clearable
              :disabled="defaultValuesDisabled"
            >
              <template v-slot:append-outer>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn small icon @click="getDefaultValues(null)" v-on="on">
                      <v-icon>mdi-database-check</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("Site.ContentType.InternalExternalTablesFields.GetDefaultValues") }}</span>
                </v-tooltip>
              </template>
            </v-select>

            <v-tooltip v-if="defaultValuesDisabled" top>
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="warning"
                  class="ml-2"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t("Site.ContentType.InternalExternalTablesFields.DefaultValuesDisabled") }}</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
      <v-row justify="end" class="align-center">
        <v-col cols="4">
          <v-select
            :loading="loading"
            :multiple="field.multiValue"
            v-bind:label="$t('Test')"
            item-text="text"
            item-value="id"
            :items="testItems"
            ><template v-slot:append-outer>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn small icon @click="test" v-on="on">
                    <v-icon>mdi-database-check</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("Test") }}</span>
              </v-tooltip>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </v-card-text>
    <ListFieldParamsModal
      :ref="listFieldParamsModalKey"
      @on-saved="executeTestQuery"
    />
  </v-card>
</template>

<script>
import Utils from '@/ecm/common/Utils';
import ContentTypeService from '@/services/ContentTypeService';
import ListFieldParamsModal from '@/components/administration/contentType/fields/ListFieldParamsModal';
import { codemirror } from 'vue-codemirror';
// import base style
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/sql/sql';
import 'codemirror/theme/solarized.css';

export default {
    components: {
        codemirror,
        ListFieldParamsModal,
    },
    async created() {
        this.loading = true;
        this.checkDefaultValuesDisabled();
        if (!this.defaultValuesDisabled) await this.getDefaultValues();
        this.loading = false;
    },
    props: {
        field: {
            type: Object,
            default: () => ({}),
        },
        validator: {
            type: Object,
            default: () => ({}),
        },
        contentTypeFields: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: false,
            testItems: [],
            cmOption: {
                tabSize: 4,
                styleActiveLine: true,
                lineNumbers: true,
                line: true,
                mode: 'text/x-mssql',
                theme: 'solarized light',
            },
            listFieldParamsModalKey: 'listFieldParamsModalKey',
            selectedTab: 0,
            filters: [
                '##TOP##',
                '##LIKE##',
            ],
            selectedFilter: '',
            defaultValuesOptions: [],
            defaultValuesDisabled: false
        };
    },
    computed: {
        cnnStrErrors() {
            const errors = [];
            if (!this.validator.field.cnnStr.$dirty) return errors;
            !this.validator.field.cnnStr.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
    },
    watch: {
        field: {
            immediate: false,
            async handler(val, oldVal) {
                this.checkDefaultValuesDisabled();
                if (!this.defaultValuesDisabled) await this.getDefaultValues(val.default);
            },
        }
    },
    methods: {
        test() {
            this.testItems = [];
            const paramItems = this.contentTypeFields
                .filter((f) => this.field.command.includes(`{${f.code}}`))
                .map((f) => {
                    return {
                        code: f.code,
                        name: f.name,
                        multiValue: f.multiValue,
                        value: '',
                    };
                });

            if ((this.selectedTab === 1 && this.field.performanceQuery && this.field.performanceQuery.includes('##LIKE##')) ||
                (this.selectedTab === 3 && this.field.noResultPerformanceQuery && this.field.noResultPerformanceQuery.includes('##LIKE##'))) {
                paramItems.push({
                    code: '##LIKE##',
                    name: 'LIKE',
                    multiValue: false,
                    value: '',
                });
            }

            if (paramItems.length > 0) {
                this.$refs[this.listFieldParamsModalKey].edit(paramItems);
            } else {
                this.executeTestQuery(paramItems);
            }
        },
        executeTestQuery(paramItems) {
            if (this.selectedTab === 1 && (!this.field.performanceQuery || this.filters.every(filter => !this.field.performanceQuery.includes(filter)))) {
                Utils.toast.error(this.$t('Site.ContentType.InternalExternalTablesFields.ErrorInPerformanceQuery'));
                return;
            } else if (this.selectedTab === 3 && (!this.field.noResultPerformanceQuery || this.filters.every(filter => !this.field.noResultPerformanceQuery.includes(filter)))) {
                Utils.toast.error(this.$t('Site.ContentType.InternalExternalTablesFields.ErrorInPerformanceQuery'));
                return;
            }

            const test = {
                ConnectionString: this.field.cnnStr,
                Query: this.selectedTab === 0 ? this.field.command : '',
                PerformanceQuery: this.selectedTab === 1 ? this.field.performanceQuery : '',
                NoResultQuery: this.selectedTab === 2 ? this.field.noResultQuery : '',
                NoResultPerformanceQuery: this.selectedTab === 3 ? this.field.noResultPerformanceQuery : '',
                UseODBC: this.field.useOdbc,
                QueryToExecute: this.selectedTab + 1,
                Parameters: paramItems.map((p) => {
                    return {
                        Name: p.code,
                        Value: p.value,
                        Multivalue: p.multiValue,
                    };
                }),
            };
            this.loading = true;
            ContentTypeService.testDynamicList(test)
                .then((data) => {
                    Utils.toast.success(this.$t('SuccessfulTest'));
                    this.testItems = data;
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.loading = false;
                });
        },
        addPerformanceQueryFilter() {
            if (this.selectedTab == 1) // Performance query
                this.field.performanceQuery = this.field.performanceQuery ? this.field.performanceQuery + this.selectedFilter : this.selectedFilter;
            else
                this.field.noResultPerformanceQuery = this.field.noResultPerformanceQuery ? this.field.noResultPerformanceQuery + this.selectedFilter : this.selectedFilter;
        },
        async getDefaultValues(defaultValue) {
            if (this.field.id === 0 && (!this.field.command || !this.field.cnnStr)) return;
            this.defaultValuesOptions = [];

            const test = {
                ConnectionString: this.field.cnnStr,
                Query: this.field.command ? this.field.command : '',
                NoResultQuery: this.field.noResultQuery ? this.field.noResultQuery : '',
                UseODBC: this.field.useOdbc,
                QueryToExecute: this.getQueryToExecute(),
                Parameters: [],
            };

            this.loading = true;
            try {
                this.defaultValuesOptions = await ContentTypeService.testDynamicList(test);
                this.defaultValuesOptions = this.defaultValuesOptions.filter((f) => f.text !== '');
                if (defaultValue)
                    this.field.default = defaultValue;
                this.validateDefaultValue();
            } catch (error) {
                // error
            } finally {
                this.loading = false;
            }
        },
        validateDefaultValue() {
            const values = Array.isArray(this.field.default) ? this.field.default : this.field.default.split(',');
            const validValues = values.filter(value =>
                this.defaultValuesOptions.some(option => option.text === value)
            );
            this.field.default = this.field.multiValue ? validValues : validValues.join(',');
        },
        getQueryToExecute() {
            if (this.field.command) return 1;
            return 3;
        },
        multiValue() {
            if (this.field.default) {
                if (!Array.isArray(this.field.default)) {
                    this.field.default = this.field.default.split(',');
                } else {
                    this.field.default = this.field.default[0];
                }
            }
        },
        queryHasChanged() {
            this.field.default = '';
            this.defaultValuesOptions = [];
            this.checkDefaultValuesDisabled();
        },
        checkDefaultValuesDisabled() {
            const hasPerformanceQuery = !!this.field.performanceQuery || !!this.field.noResultPerformanceQuery;
            const commandContainsBracket = this.field.command ? this.field.command.includes('{') : false;
            const noResultQueryContainsBracket = this.field.noResultQuery ? this.field.noResultQuery.includes('{') : false;
            this.defaultValuesDisabled = hasPerformanceQuery || commandContainsBracket || noResultQueryContainsBracket;
        }
    },
};
</script>

<style lang="scss">
.sql-editor {
  .CodeMirror {
    border: 1px solid #eee;
    height: auto !important;
    min-height: 200px !important;
    /*max-height: 500px;*/
    line-height: 20px;
  }
  .CodeMirror-scroll {
    min-height: 200px;
  }
}
</style>
